import { CheckCircleFilled, CloseCircleFilled, FilterOutlined, WarningFilled } from "@ant-design/icons";
import { Flex, Select, Table, Tag, Tooltip, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import type { ClinicianNoteRow } from "../../service/compliance.service";

const { Text } = Typography;

type Props = {
  clinicianUserId: number;
  notes: ClinicianNoteRow[];
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  loading: boolean;
  selectedClientUuid?: string;
  onClientFilterChange: (clientUuid: string | undefined) => void;
  clients: { uuid: string; name: string }[];
  hasActiveRules: boolean;
};

const getStatusColor = (status: number) => {
  switch (status) {
    case 1:
      return "success";
    case 0:
    case 2:
    case 3:
    case 4:
      return "processing";
    case 5:
      return "error";
    default:
      return "default";
  }
};

const getStatusLabel = (status: number) => {
  switch (status) {
    case 0:
      return "Processing";
    case 1:
      return "Complete";
    case 2:
      return "Uploading";
    case 3:
      return "Transcribing";
    case 4:
      return "Generating";
    case 5:
      return "Canceled";
    default:
      return "Unknown";
  }
};

const ComplianceCell = ({ row, hasActiveRules }: { row: ClinicianNoteRow; hasActiveRules: boolean }) => {
  const compliance = row.compliance;
  if (!compliance || compliance.status === null) {
    const tooltip = hasActiveRules ? "No rule sets attached to this note's template" : "Compliance not yet set up";
    return (
      <Tooltip title={tooltip}>
        <Text style={{ color: "#9ca3af" }}>—</Text>
      </Tooltip>
    );
  }
  if (compliance.status === "pass") {
    return (
      <Tooltip title={`${compliance.pass_count} rules passed`}>
        <Flex align="center" gap={6}>
          <CheckCircleFilled style={{ color: "#10b981" }} />
          <Text style={{ color: "#047857", fontWeight: 500 }}>Clean</Text>
        </Flex>
      </Tooltip>
    );
  }
  if (compliance.status === "partial") {
    return (
      <Tooltip title={`${compliance.partial_count} partial · ${compliance.pass_count} pass`}>
        <Flex align="center" gap={6}>
          <WarningFilled style={{ color: "#d97706" }} />
          <Text style={{ color: "#b45309", fontWeight: 500 }}>{compliance.partial_count} partial</Text>
        </Flex>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={`${compliance.fail_count} fail · ${compliance.partial_count} partial · ${compliance.pass_count} pass`}
    >
      <Flex align="center" gap={6}>
        <CloseCircleFilled style={{ color: "#dc2626" }} />
        <Text style={{ color: "#b91c1c", fontWeight: 500 }}>{compliance.fail_count} fail</Text>
      </Flex>
    </Tooltip>
  );
};

const ComplianceNotesTable = ({
  clinicianUserId,
  notes,
  total,
  currentPage,
  pageSize,
  onPageChange,
  loading,
  selectedClientUuid,
  onClientFilterChange,
  clients,
  hasActiveRules,
}: Props) => {
  const navigate = useNavigate();

  const handleNoteClick = (note: ClinicianNoteRow) => {
    if (note.status !== 1) {
      message.info("This note is still processing");
      return;
    }
    navigate(`/compliance/clinicians/${clinicianUserId}/notes/${note.id}`);
  };

  const columns: ColumnsType<ClinicianNoteRow> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title: string | null, record) => <Text strong>{title || `Note #${record.id}`}</Text>,
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: (name: string | null) => <Text>{name || "—"}</Text>,
    },
    {
      title: "Note Type",
      dataIndex: "note_type_label",
      key: "note_type_label",
      render: (label: string) => <Text>{label}</Text>,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (date: string) => <Text>{new Date(date).toLocaleString()}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: number) => <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>,
    },
    {
      title: "Compliance",
      key: "compliance",
      width: 160,
      render: (_value, row) => <ComplianceCell row={row} hasActiveRules={hasActiveRules} />,
    },
  ];

  const clientOptions = [
    { label: "All Clients", value: undefined as string | undefined },
    ...clients.map((c) => ({ label: c.name, value: c.uuid })),
  ];

  return (
    <div>
      <Flex justify="flex-end" style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 250 }}
          placeholder="Filter by client"
          value={selectedClientUuid}
          onChange={onClientFilterChange}
          options={clientOptions}
          suffixIcon={<FilterOutlined />}
          allowClear
        />
      </Flex>

      <Table
        columns={columns}
        dataSource={notes}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          current: currentPage,
          pageSize,
          showSizeChanger: false,
          onChange: onPageChange,
        }}
        locale={{ emptyText: "No notes found for this clinician." }}
        onRow={(record) => ({
          onClick: () => handleNoteClick(record),
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default ComplianceNotesTable;
